import React, { useState, useEffect, useCallback } from 'react';
import { Handle, Position } from '@xyflow/react';
import { SlidersHorizontal, Search, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { sheetsService, SchemaColumn, SearchCriteria } from '../../../services/sheetsService';
import { s3Config } from '../../../aws-config';
import { useUpstreamFile } from '../useUpstreamFile';

export const FilterNode: React.FC<{ id: string; data: { label: string; color: string; isTemplate?: boolean } }> = ({ id, data }) => {
  const selectedFile = useUpstreamFile(id);
  const [schema, setSchema] = useState<SchemaColumn[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterColumn, setFilterColumn] = useState('');
  const [filterOperator, setFilterOperator] = useState<SearchCriteria['operator']>('contains');
  const [filterValue, setFilterValue] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [searching, setSearching] = useState(false);

  const s3Key = selectedFile?.s3Key || `users/placeholder/${selectedFile?.fileName}`;
  const bucket = s3Config.primaryDataBucket;

  useEffect(() => {
    if (!selectedFile) {
      setSchema([]);
      setResults([]);
      setMatchCount(null);
      return;
    }
    setLoading(true);
    sheetsService
      .getSchema(bucket, s3Key, { isMultipart: selectedFile.isMultipart })
      .then((res) => {
        if (res.success && res.schema) setSchema(res.schema);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    setFilterColumn('');
    setFilterValue('');
    setResults([]);
    setMatchCount(null);
  }, [selectedFile, bucket, s3Key]);

  const handleFilter = useCallback(async () => {
    if (!selectedFile || !filterColumn || !filterValue.trim()) return;
    setSearching(true);
    try {
      const res = await sheetsService.search(
        bucket,
        s3Key,
        { column: filterColumn, operator: filterOperator, value: filterValue },
        { limit: 20, offset: 0, isMultipart: selectedFile.isMultipart }
      );
      if (res.success && res.data) {
        setResults(res.data.slice(0, 5));
        setMatchCount(res.matched_rows ?? res.data.length);
      }
    } catch {
      // ignore
    } finally {
      setSearching(false);
    }
  }, [selectedFile, bucket, s3Key, filterColumn, filterOperator, filterValue]);

  const searchDisabled = searching || !filterColumn || !filterValue.trim();

  return (
    <div className="bg-card border rounded-xl shadow-md w-[350px] overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-1.5"
        style={{ background: data.color }}
      >
        <SlidersHorizontal className="h-3.5 w-3.5" />
        {data.label}
      </div>

      <div className="p-3 space-y-2">
        {!selectedFile ? (
          <p className="text-[11px] text-muted-foreground text-center py-2">
            Connect a data source
          </p>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded-md px-2 py-1">
            <span className="truncate">{selectedFile.fileName}</span>
          </div>
        )}

        {selectedFile && loading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {selectedFile && !loading && schema.length > 0 && (
          <>
            <div className="flex items-center gap-1 nodrag">
              <Select value={filterColumn} onValueChange={setFilterColumn}>
                <SelectTrigger className="h-7 text-[11px] w-[120px]">
                  <SelectValue placeholder="Column" />
                </SelectTrigger>
                <SelectContent>
                  {schema.map((c) => (
                    <SelectItem key={c.name} value={c.name} className="text-xs">
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterOperator} onValueChange={(v) => setFilterOperator(v as SearchCriteria['operator'])}>
                <SelectTrigger className="h-7 text-[11px] w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(['contains', 'eq', 'ne', 'gt', 'lt', 'starts_with', 'ends_with'] as const).map((op) => (
                    <SelectItem key={op} value={op} className="text-xs">
                      {op}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1 nodrag">
              <Input
                className="h-7 text-[11px] flex-1"
                placeholder="Value..."
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !searchDisabled && handleFilter()}
              />
              <Button
                variant="secondary"
                size="sm"
                className="h-7 text-[11px] px-2"
                onClick={handleFilter}
                disabled={searchDisabled}
              >
                {searching ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Apply'}
              </Button>
            </div>

            {matchCount !== null && (
              <Badge variant="outline" className="text-[10px]">
                {matchCount.toLocaleString()} matches
              </Badge>
            )}

            {results.length > 0 && (
              <div className="space-y-1 max-h-[150px] overflow-auto nodrag nowheel">
                {results.map((r, i) => (
                  <div key={i} className="bg-muted/30 rounded px-2 py-1 text-[11px] truncate">
                    {Object.values(r).filter(Boolean).join(' | ').slice(0, 120)}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Handle type="target" position={Position.Left} className="!bg-muted-foreground" />
      <Handle type="source" position={Position.Right} className="!bg-muted-foreground" />
    </div>
  );
};
